import React, { useCallback, useEffect, useState } from 'react';
import { FiBarChart2, FiDownload, FiX } from 'react-icons/fi';
import SessionService from '../services/SessionService';
import { listUnifiedAgents, type UnifiedAiAgentDTO } from '../services/UnifiedAIAgentService';
import {
  cancelEvalRun,
  compareEvalRuns,
  createEvalRun,
  deleteEvalRun,
  fetchEvalArtifact,
  getEvalRun,
  listEvalDatasets,
  listEvalModels,
  listEvalRuns,
  parseSummary,
  type EvalAggregate,
  type EvalDatasetInfo,
  type EvalEpochRow,
  type EvalRunDetail,
  type EvalRunRow,
  type EvalSummary,
} from '../services/AgentEvalService';
import './AiSandboxPanel.css';
import './AgentEvalPanel.css';

interface AgentEvalPanelProps {
  sectorId: number;
  onClose: () => void;
}

type Workspace = 'results' | 'compare';
type ResultTab = 'stats' | 'chats';
type ChatFilter = 'all' | 'pass' | 'fail';

function pct(value: number): string {
  return `${Math.round((value || 0) * 100)}%`;
}

function num(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return value.toFixed(2);
}

function modelLabel(run: EvalRunRow): string {
  const id = run.model?.trim();
  return id || 'produção';
}

function statusLabel(status: string): string {
  switch (status) {
    case 'queued': return 'Na fila';
    case 'running': return 'Rodando';
    case 'done': return 'Concluída';
    case 'failed': return 'Falhou';
    case 'cancelled': return 'Cancelada';
    default: return status;
  }
}

const AgentEvalPanel: React.FC<AgentEvalPanelProps> = ({ sectorId, onClose }) => {
  const token = SessionService.getToken() ?? '';
  const [agents, setAgents] = useState<UnifiedAiAgentDTO[]>([]);
  const [datasets, setDatasets] = useState<EvalDatasetInfo[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [agentId, setAgentId] = useState<number | ''>('');
  const [datasetId, setDatasetId] = useState('jmc-core');
  const [model, setModel] = useState('');
  const [epochs, setEpochs] = useState(10);
  const [promptOverride, setPromptOverride] = useState('');
  const [simulator, setSimulator] = useState(false);
  const [pdfVisual, setPdfVisual] = useState(false);
  const [runs, setRuns] = useState<EvalRunRow[]>([]);
  const [detail, setDetail] = useState<EvalRunDetail | null>(null);
  const [workspace, setWorkspace] = useState<Workspace>('results');
  const [resultTab, setResultTab] = useState<ResultTab>('stats');
  const [chatFilter, setChatFilter] = useState<ChatFilter>('all');
  const [compareLeft, setCompareLeft] = useState<number | ''>('');
  const [compareRight, setCompareRight] = useState<number | ''>('');
  const [comparison, setComparison] = useState<{ left: EvalSummary; right: EvalSummary; leftRun: EvalRunRow; rightRun: EvalRunRow } | null>(null);
  const [previews, setPreviews] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const refreshRuns = useCallback(async () => {
    const list = await listEvalRuns(sectorId, token);
    setRuns(list);
    return list;
  }, [sectorId, token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [agentList, datasetList, modelInfo, runList] = await Promise.all([
          listUnifiedAgents(sectorId, token),
          listEvalDatasets(token),
          listEvalModels(token),
          listEvalRuns(sectorId, token),
        ]);
        if (!alive) return;
        const usable = agentList.filter(a => a.capReceptive || a.capCampaign);
        setAgents(usable);
        if (usable.length > 0) setAgentId(usable[0].id);
        setDatasets(datasetList);
        if (datasetList.some(d => d.id === 'jmc-core')) setDatasetId('jmc-core');
        else if (datasetList[0]) setDatasetId(datasetList[0].id);
        setModels(modelInfo.models);
        setModel(modelInfo.production || '');
        setRuns(runList);
      } catch {
        if (alive) setError('Não foi possível carregar os dados de eval. A migration agent_eval precisa estar aplicada.');
      }
    })();
    return () => { alive = false; };
  }, [sectorId, token]);

  const openRun = useCallback(async (id: number) => {
    const next = await getEvalRun(id, token, true);
    setDetail(next);
    const pngs = next.epochs.flatMap(e => e.artifacts.filter(a => a.kind === 'png').map(a => a.id));
    const urls: Record<number, string> = {};
    for (const artifactId of pngs.slice(0, 12)) {
      try {
        const blob = await fetchEvalArtifact(artifactId, token);
        urls[artifactId] = URL.createObjectURL(blob);
      } catch {
        /* preview is optional */
      }
    }
    setPreviews(prev => {
      Object.values(prev).forEach(url => URL.revokeObjectURL(url));
      return urls;
    });
  }, [token]);

  const runId = detail?.run.id;
  const runStatus = detail?.run.status;

  useEffect(() => {
    if (!runId || (runStatus !== 'queued' && runStatus !== 'running')) return;
    const timer = window.setInterval(async () => {
      try {
        const next = await getEvalRun(runId, token, false);
        await refreshRuns();
        if (next.run.status !== 'queued' && next.run.status !== 'running') {
          window.clearInterval(timer);
          await openRun(next.run.id);
          return;
        }
        setDetail(next);
      } catch {
        /* keep the last snapshot */
      }
    }, 2500);
    return () => window.clearInterval(timer);
  }, [runId, runStatus, openRun, refreshRuns, token]);

  useEffect(() => () => {
    Object.values(previews).forEach(url => URL.revokeObjectURL(url));
  }, [previews]);

  const selectRun = async (id: number) => {
    setWorkspace('results');
    setResultTab('stats');
    setChatFilter('all');
    setConfirmDeleteId(null);
    setError(null);
    try {
      await openRun(id);
    } catch (err: unknown) {
      setError(axiosMessage(err) || 'Não foi possível abrir a run.');
    }
  };

  const removeRun = async (id: number) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }
    setError(null);
    try {
      await deleteEvalRun(id, token);
      setConfirmDeleteId(null);
      if (detail?.run.id === id) setDetail(null);
      if (compareLeft === id) setCompareLeft('');
      if (compareRight === id) setCompareRight('');
      setComparison(current => (
        current && (current.leftRun.id === id || current.rightRun.id === id) ? null : current
      ));
      await refreshRuns();
    } catch (err: unknown) {
      setError(axiosMessage(err) || 'Não foi possível excluir a run.');
    }
  };

  const start = async () => {
    if (!agentId) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createEvalRun({
        sectorId,
        agentId: Number(agentId),
        datasetId,
        epochs,
        model: model || undefined,
        promptOverride: promptOverride.trim() || undefined,
        rubricJudge: true,
        pdfVisual,
        simulator: { enabled: simulator, maxTurns: 6 },
      }, token);
      setWorkspace('results');
      setResultTab('stats');
      await refreshRuns();
      await openRun(created.id);
    } catch (err: unknown) {
      setError(axiosMessage(err) || 'Falha ao criar o run.');
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async (artifactId: number) => {
    const blob = await fetchEvalArtifact(artifactId, token);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cotacao-eval-${artifactId}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const compare = async () => {
    if (!compareLeft || !compareRight || compareLeft === compareRight) return;
    setComparing(true);
    setError(null);
    try {
      const result = await compareEvalRuns(Number(compareLeft), Number(compareRight), token);
      setComparison({
        left: result.left.summary,
        right: result.right.summary,
        leftRun: result.left.run,
        rightRun: result.right.run,
      });
    } catch (err: unknown) {
      setError(axiosMessage(err) || 'Falha ao comparar.');
    } finally {
      setComparing(false);
    }
  };

  const summary = parseSummary(detail?.run.summaryJson);
  const progress = detail && detail.run.progressTotal > 0
    ? Math.round((detail.run.progressDone / detail.run.progressTotal) * 100)
    : 0;
  const visibleEpochs = (detail?.epochs ?? []).filter(epoch => {
    if (chatFilter === 'pass') return epoch.pass;
    if (chatFilter === 'fail') return !epoch.pass;
    return true;
  });

  return (
    <div className="ai-sandbox-overlay ai-eval-overlay" role="dialog" aria-label="Eval de agentes">
      <div className="ai-sandbox-modal ai-eval-modal">
        <header className="ai-sandbox-header">
          <div className="ai-sandbox-header__title">
            <h2><FiBarChart2 /> Eval de agentes</h2>
            <p>Roda o agente nos casos e mostra o que passou.</p>
          </div>
          <button type="button" className="ai-sandbox-close" onClick={onClose} aria-label="Fechar">
            <FiX />
          </button>
        </header>

        <div className="ai-eval-body">
          <section className="ai-eval-form" aria-label="Nova run">
            <label>
              Agente
              <select value={agentId} onChange={e => setAgentId(Number(e.target.value))}>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>{agent.name}</option>
                ))}
              </select>
            </label>
            <label>
              Dataset
              <select value={datasetId} onChange={e => setDatasetId(e.target.value)}>
                {datasets.map(ds => (
                  <option key={ds.id} value={ds.id}>{ds.label} ({ds.caseCount})</option>
                ))}
              </select>
            </label>
            <label>
              Modelo
              <input
                list="eval-models"
                value={model}
                placeholder="provider/modelo"
                onChange={e => setModel(e.target.value)}
              />
              <datalist id="eval-models">
                {models.map(item => <option key={item} value={item} />)}
              </datalist>
            </label>
            <label>
              Repetições
              <input type="number" min={1} max={30} value={epochs} onChange={e => setEpochs(Number(e.target.value))} />
            </label>
            <button type="button" className="ai-eval-start" onClick={start} disabled={busy || !agentId}>
              {busy ? 'Enfileirando…' : 'Rodar eval'}
            </button>
            <p className="ai-eval-hint ai-eval-wide">
              Modelo no formato do gateway, como openai/gpt-4o. Vazio usa o de produção.
            </p>
            <details className="ai-eval-more ai-eval-wide">
              <summary>Mais opções</summary>
              <div className="ai-eval-more__body">
                <label>
                  Prompt desta run
                  <textarea
                    value={promptOverride}
                    onChange={e => setPromptOverride(e.target.value)}
                    rows={3}
                    placeholder="Vazio usa o prompt do agente"
                  />
                </label>
                <label className="ai-eval-check">
                  <input type="checkbox" checked={simulator} onChange={e => setSimulator(e.target.checked)} />
                  Simular cliente nos casos abertos
                </label>
                <label className="ai-eval-check">
                  <input type="checkbox" checked={pdfVisual} onChange={e => setPdfVisual(e.target.checked)} />
                  Pedir nota visual do PDF
                </label>
              </div>
            </details>
          </section>

          {error && <p className="ai-eval-error" role="alert">{error}</p>}

          <div className="ai-eval-workspace" role="tablist" aria-label="Análise">
            <button
              type="button"
              role="tab"
              aria-selected={workspace === 'results'}
              className={workspace === 'results' ? 'is-selected' : ''}
              onClick={() => setWorkspace('results')}
            >
              Resultados
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={workspace === 'compare'}
              className={workspace === 'compare' ? 'is-selected' : ''}
              onClick={() => setWorkspace('compare')}
            >
              Comparar
            </button>
          </div>

          {workspace === 'results' && (
            <section className="ai-eval-runs" aria-label="Runs deste setor">
              {runs.length === 0 ? (
                <p className="ai-eval-empty">Nenhuma run neste setor. Use Rodar eval acima.</p>
              ) : (
                <ul>
                  {runs.map(run => (
                    <li key={run.id}>
                      <button
                        type="button"
                        aria-pressed={detail?.run.id === run.id}
                        className={detail?.run.id === run.id ? 'is-selected' : ''}
                        onClick={() => selectRun(run.id)}
                      >
                        <strong>#{run.id}</strong>
                        <span>{modelLabel(run)}</span>
                        <span>{run.datasetId}</span>
                        <span>{statusLabel(run.status)}</span>
                        <span>{run.progressDone}/{run.progressTotal}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {!detail && runs.length > 0 && (
                <p className="ai-eval-empty">Selecione uma run para ver estatísticas e conversas.</p>
              )}

              {detail && (
                <div className="ai-eval-detail">
                  <div className="ai-eval-runhead">
                    <div>
                      <h3>Run #{detail.run.id} · {statusLabel(detail.run.status)}</h3>
                      <p className="ai-eval-model">{modelLabel(detail.run)}</p>
                    </div>
                    <div className="ai-eval-runhead__actions">
                      {(detail.run.status === 'queued' || detail.run.status === 'running') && (
                        <button
                          type="button"
                          onClick={() => cancelEvalRun(detail.run.id, token).then(() => openRun(detail.run.id))}
                        >
                          Cancelar run
                        </button>
                      )}
                      <button
                        type="button"
                        className={confirmDeleteId === detail.run.id ? 'is-danger' : ''}
                        onClick={() => removeRun(detail.run.id)}
                      >
                        {confirmDeleteId === detail.run.id ? 'Confirmar exclusão' : 'Excluir run'}
                      </button>
                    </div>
                  </div>
                  <div className="ai-eval-progress" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} role="progressbar">
                    <span style={{ width: `${progress}%` }} />
                  </div>
                  <p className="ai-eval-progress-label">{detail.run.progressDone} de {detail.run.progressTotal}</p>
                  {detail.run.error && <p className="ai-eval-error">{detail.run.error}</p>}

                  <div className="ai-eval-subtabs" role="tablist" aria-label={`Run ${detail.run.id}`}>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={resultTab === 'stats'}
                      className={resultTab === 'stats' ? 'is-selected' : ''}
                      onClick={() => setResultTab('stats')}
                    >
                      Estatísticas
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={resultTab === 'chats'}
                      className={resultTab === 'chats' ? 'is-selected' : ''}
                      onClick={() => setResultTab('chats')}
                    >
                      Conversas
                    </button>
                  </div>

                  {resultTab === 'stats' && (
                    <div role="tabpanel">
                      {!summary && (
                        <p className="ai-eval-empty">As estatísticas aparecem quando a run termina.</p>
                      )}
                      {summary && (
                        <>
                          <div className="ai-eval-stats">
                            <StatBlock title="Roteiro" stats={summary.scripted} />
                            <StatBlock title="Simulado" stats={summary.simulated} />
                          </div>
                          {(summary.cases?.length ?? 0) > 0 && (
                            <table className="ai-eval-table">
                              <thead>
                                <tr>
                                  <th>Caso</th><th>Modo</th><th>n</th><th>Aprovação</th>
                                  <th>p10</th><th>p50</th><th>p90</th>
                                </tr>
                              </thead>
                              <tbody>
                                {summary.cases.map(row => (
                                  <tr key={`${row.mode}-${row.caseId}`}>
                                    <td>{row.caseId}</td>
                                    <td>{row.mode === 'simulated' ? 'Simulado' : 'Roteiro'}</td>
                                    <td>{row.stats.n}</td>
                                    <td>{pct(row.stats.passRate)}</td>
                                    <td>{num(row.stats.p10Score)}</td>
                                    <td>{num(row.stats.p50Score)}</td>
                                    <td>{num(row.stats.p90Score)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {resultTab === 'chats' && (
                    <div role="tabpanel">
                      <div className="ai-eval-filters" role="group" aria-label="Filtrar conversas">
                        {(['all', 'pass', 'fail'] as ChatFilter[]).map(filter => (
                          <button
                            key={filter}
                            type="button"
                            aria-pressed={chatFilter === filter}
                            className={chatFilter === filter ? 'is-selected' : ''}
                            onClick={() => setChatFilter(filter)}
                          >
                            {filter === 'all' ? 'Todas' : filter === 'pass' ? 'Passou' : 'Falhou'}
                          </button>
                        ))}
                      </div>
                      {visibleEpochs.length === 0 && (
                        <p className="ai-eval-empty">
                          {detail.epochs.length === 0
                            ? 'Esta run ainda não tem conversas.'
                            : 'Nenhuma conversa neste filtro.'}
                        </p>
                      )}
                      <div className="ai-eval-epochs">
                        {visibleEpochs.map(epoch => (
                          <EpochCard
                            key={epoch.id}
                            epoch={epoch}
                            previews={previews}
                            onDownload={downloadPdf}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {workspace === 'compare' && (
            <ComparePane
              runs={runs}
              leftId={compareLeft}
              rightId={compareRight}
              onLeft={setCompareLeft}
              onRight={setCompareRight}
              onCompare={compare}
              comparing={comparing}
              comparison={comparison}
            />
          )}
        </div>
      </div>
    </div>
  );
};

function StatBlock({ title, stats }: { title: string; stats: EvalAggregate }) {
  return (
    <div>
      <h4>{title}</h4>
      <p>Aprovação {pct(stats.passRate)} · n {stats.n} · erros {stats.transportErrors}</p>
      <p>Nota p10 {num(stats.p10Score)} · p50 {num(stats.p50Score)} · p90 {num(stats.p90Score)}</p>
      <p>Latência p50 {Math.round(stats.p50LatencyMs)} ms</p>
    </div>
  );
}

function ComparePane({
  runs,
  leftId,
  rightId,
  onLeft,
  onRight,
  onCompare,
  comparing,
  comparison,
}: {
  runs: EvalRunRow[];
  leftId: number | '';
  rightId: number | '';
  onLeft: (id: number | '') => void;
  onRight: (id: number | '') => void;
  onCompare: () => void;
  comparing: boolean;
  comparison: { left: EvalSummary; right: EvalSummary; leftRun: EvalRunRow; rightRun: EvalRunRow } | null;
}) {
  const same = leftId !== '' && leftId === rightId;
  return (
    <section className="ai-eval-compare" aria-label="Comparar runs">
      <div className="ai-eval-compare__pickers">
        <label>
          Run A
          <select value={leftId} onChange={e => onLeft(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Escolher</option>
            {runs.map(run => (
              <option key={run.id} value={run.id}>#{run.id} {modelLabel(run)} · {run.datasetId} · {statusLabel(run.status)}</option>
            ))}
          </select>
        </label>
        <label>
          Run B
          <select value={rightId} onChange={e => onRight(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Escolher</option>
            {runs.map(run => (
              <option key={run.id} value={run.id}>#{run.id} {modelLabel(run)} · {run.datasetId} · {statusLabel(run.status)}</option>
            ))}
          </select>
        </label>
        <button type="button" className="ai-eval-start" onClick={onCompare} disabled={!leftId || !rightId || same || comparing}>
          {comparing ? 'Comparando…' : 'Comparar'}
        </button>
      </div>
      {same && <p className="ai-eval-error">Escolha duas runs diferentes.</p>}
      {!comparison && !same && (
        <p className="ai-eval-empty">Escolha duas runs do mesmo setor.</p>
      )}
      {comparison && <CompareBoard comparison={comparison} />}
    </section>
  );
}

function CompareBoard({
  comparison,
}: {
  comparison: { left: EvalSummary; right: EvalSummary; leftRun: EvalRunRow; rightRun: EvalRunRow };
}) {
  const passDelta = Math.round((comparison.right.scripted.passRate - comparison.left.scripted.passRate) * 100);
  const deltaText = passDelta === 0
    ? 'Aprovação do roteiro igual nas duas runs.'
    : passDelta > 0
      ? `Run B aprova ${passDelta} pontos a mais que A no roteiro.`
      : `Run B aprova ${Math.abs(passDelta)} pontos a menos que A no roteiro.`;
  const keys = new Map<string, { caseId: string; mode: string; left?: EvalAggregate; right?: EvalAggregate }>();
  for (const row of comparison.left.cases ?? []) {
    keys.set(`${row.mode}:${row.caseId}`, { caseId: row.caseId, mode: row.mode, left: row.stats });
  }
  for (const row of comparison.right.cases ?? []) {
    const key = `${row.mode}:${row.caseId}`;
    const current = keys.get(key) ?? { caseId: row.caseId, mode: row.mode };
    current.right = row.stats;
    keys.set(key, current);
  }

  return (
    <div className="ai-eval-compare-board">
      <p className="ai-eval-compare-delta">{deltaText}</p>
      <div className="ai-eval-compare-cols">
        <CompareColumn label="A" run={comparison.leftRun} summary={comparison.left} />
        <CompareColumn label="B" run={comparison.rightRun} summary={comparison.right} />
      </div>
      {keys.size > 0 && (
        <table className="ai-eval-table">
          <thead>
            <tr>
              <th>Caso</th>
              <th>A aprovação</th>
              <th>B aprovação</th>
              <th>A p50</th>
              <th>B p50</th>
            </tr>
          </thead>
          <tbody>
            {Array.from(keys.values()).map(row => (
              <tr key={`${row.mode}-${row.caseId}`}>
                <td>{row.caseId}</td>
                <td>{row.left ? pct(row.left.passRate) : '—'}</td>
                <td>{row.right ? pct(row.right.passRate) : '—'}</td>
                <td>{row.left ? num(row.left.p50Score) : '—'}</td>
                <td>{row.right ? num(row.right.p50Score) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CompareColumn({ label, run, summary }: { label: string; run: EvalRunRow; summary: EvalSummary }) {
  return (
    <div>
      <h4>{label} · #{run.id}</h4>
      <p>{modelLabel(run)}</p>
      <p>{run.datasetId} · {statusLabel(run.status)}</p>
      <p>Roteiro {pct(summary.scripted.passRate)} · p50 {num(summary.scripted.p50Score)} · {Math.round(summary.scripted.p50LatencyMs)} ms</p>
      <p>Simulado {pct(summary.simulated.passRate)} · p50 {num(summary.simulated.p50Score)}</p>
    </div>
  );
}

function EpochCard({
  epoch,
  previews,
  onDownload,
}: {
  epoch: EvalEpochRow;
  previews: Record<number, string>;
  onDownload: (id: number) => void;
}) {
  const png = epoch.artifacts.find(a => a.kind === 'png');
  const pdf = epoch.artifacts.find(a => a.kind === 'pdf');
  return (
    <article className={epoch.pass ? 'ai-eval-epoch ai-eval-epoch--pass' : 'ai-eval-epoch'}>
      <header>
        <strong>{epoch.caseId}</strong>
        <span>repetição {epoch.epoch} · {epoch.mode === 'simulated' ? 'simulado' : 'roteiro'} · {epoch.model?.trim() || 'produção'} · {epoch.pass ? 'passou' : 'falhou'} · {num(epoch.score)}</span>
      </header>
      {epoch.transportError && <p>Erro de transporte: {epoch.transportError}</p>}
      {!pdf && epoch.pdfGateReason && epoch.pdfGateReason !== 'no_payload' && (
        <p>PDF não gerado: {epoch.pdfGateReason}</p>
      )}
      <EpochTranscript json={epoch.transcriptJson} />
      {png && previews[png.id] && <img src={previews[png.id]} alt={`Preview ${epoch.caseId}`} />}
      {pdf && (
        <button type="button" onClick={() => onDownload(pdf.id)}>
          <FiDownload /> Baixar PDF
        </button>
      )}
    </article>
  );
}

function EpochTranscript({ json }: { json?: string | null }) {
  if (!json) return <p className="ai-eval-empty">Conversa ainda não gravada.</p>;
  let lines: { role: string; text: string }[] = [];
  try {
    const rows = JSON.parse(json) as { role?: string; text?: string }[];
    if (Array.isArray(rows)) {
      lines = rows
        .filter(row => (row.role === 'customer' || row.role === 'assistant') && row.text)
        .map(row => ({
          role: row.role === 'customer' ? 'Cliente' : 'Atendente',
          text: row.text ?? '',
        }));
    }
  } catch {
    return null;
  }
  if (lines.length === 0) return null;
  return (
    <div className="ai-eval-transcript">
      {lines.map((line, index) => (
        <p key={index} className={line.role === 'Cliente' ? 'is-customer' : 'is-agent'}>
          <strong>{line.role}</strong>
          <span>{line.text}</span>
        </p>
      ))}
    </div>
  );
}

function axiosMessage(err: unknown): string | null {
  if (typeof err === 'object' && err && 'response' in err) {
    const response = (err as { response?: { data?: { message?: string } } }).response;
    return response?.data?.message ?? null;
  }
  return null;
}

export default AgentEvalPanel;
